import { ChangeDetectionStrategy, Component, Input, OnInit, computed, inject } from '@angular/core';

import { Observable, of, map } from 'rxjs';
import { Store } from '@ngrx/store';

import { IconLibrary, SvgLibraryIcon, SvgLibraryNewIcon } from '@finnairoyj/fcom-ui-styles';

import { AppState, NativeBridgeService } from '@fcom/core';
import { casProfileName, casProfileOrNot, loginStatus } from '@fcom/core/selectors';
import { CasCustomerType, CasProfile, LoginStatus } from '@fcom/core-api/login';
import { finShare } from '@fcom/rx';
import { ButtonMode, ButtonSize, ButtonTheme, IconButtonTheme, IconPosition, IconSet } from '@fcom/ui-components';

import { ElementTypes } from '../../interfaces';
import { FinnairThemeService, MediaQueryService } from '../../services';
import { FinnairTheme } from '../../interfaces/theme.interface';

const SHOW_BUTTON_STATES = [
  LoginStatus.LOGGED_IN,
  LoginStatus.NOT_LOGGED_IN,
  LoginStatus.ERROR,
  LoginStatus.PENDING,
  LoginStatus.INITIAL,
];

@Component({
  selector: 'fin-login-button',
  templateUrl: 'login-button.component.html',
  styleUrls: ['./login-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LoginButtonComponent implements OnInit {
  private store$ = inject<Store<AppState>>(Store);
  private nativeBridgeService = inject(NativeBridgeService);
  private mediaQueryService = inject(MediaQueryService);
  private themeService = inject(FinnairThemeService);

  readonly ButtonMode = ButtonMode;
  readonly ButtonSize = ButtonSize;
  readonly ButtonTheme = ButtonTheme;
  readonly IconButtonTheme = IconButtonTheme;
  readonly ElementTypes = ElementTypes;
  readonly IconPosition = IconPosition;
  readonly LoginStatus = LoginStatus;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly SvgLibraryNewIcon = SvgLibraryNewIcon;
  readonly IconLibrary = IconLibrary;

  showButton$: Observable<boolean>;
  isMobile$: Observable<boolean>;
  icon$: Observable<IconSet>;

  profileName$: Observable<string>;
  loginStatus$: Observable<LoginStatus>;

  @Input()
  showName = false;

  @Input()
  nameStyle = 'font-body-1 font-body-medium';

  @Input()
  trackingContext: string;

  isNewBrand = computed(() => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  ngOnInit(): void {
    this.profileName$ = this.store$.pipe(casProfileName());
    this.loginStatus$ = this.store$.pipe(loginStatus(), finShare());
    this.icon$ = this.store$.pipe(
      casProfileOrNot(),
      map((casProfile) => this.getIcon(casProfile))
    );
    this.isMobile$ = this.mediaQueryService.isBreakpoint$('mobile');
    this.showButton$ = this.nativeBridgeService.isInsideNativeWebView
      ? of(false)
      : this.loginStatus$.pipe(
          map((s) => SHOW_BUTTON_STATES.indexOf(s) !== -1),
          finShare()
        );
  }

  private getIcon(casProfile: CasProfile | undefined): IconSet {
    const isCorporate = casProfile?.customerType === CasCustomerType.CORPORATE_USER;

    if (!this.isNewBrand()) {
      const name = !casProfile
        ? SvgLibraryIcon.SINGLE_PAX
        : isCorporate
          ? SvgLibraryIcon.B2B
          : SvgLibraryIcon.LOGGED_IN;

      return { category: IconLibrary.SVG_LIBRARY, name };
    }

    const name = !casProfile
      ? SvgLibraryNewIcon.SINGLE_PAX
      : isCorporate
        ? SvgLibraryNewIcon.B2B
        : SvgLibraryNewIcon.PROFILE;

    return { category: IconLibrary.SVG_LIBRARY_NEW, name };
  }
}
